import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Users, Plus, Pencil, ChevronRight, AlertCircle, RotateCcw, CheckCircle2, Search, ShieldCheck, UserCog, Briefcase, GraduationCap, X, Layers, Trash2, Archive, AlertTriangle, Download } from "lucide-react";
import { ScreenShell, GlassCard, GradientText, SimHeader } from "@/components/sim/shared";
import { AdminNav } from "@/components/neusim/AdminNav";
import { apiCall, ApiError } from "@/lib/api";

interface UserRow {
  id: string;
  email: string;
  fullName: string;
  role: string;
  clientId: string | null;
  clientName: string | null;
  isActive: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  assignmentCount: number;
  attemptCount: number;
  /** Phase K2: which projects (under clientId) this client_admin can manage. */
  projectIds: string[];
}

interface AssignmentRow {
  id: string;
  simulationId: string;
  simCode: string;
  simTitle: string;
  projectName: string;
  clientName: string;
  cohortName: string;
  assignedAt: string;
  isActive: boolean;
  attemptCount: number;
}

interface ClientOpt { id: string; name: string; isActive: boolean }
interface SimOpt { id: string; simCode: string; title: string; projectName: string | null; clientName: string | null }

const ROLES: Array<{ value: string; label: string; icon: JSX.Element; color: string; bg: string }> = [
  { value: "super_admin", label: "Super admin", icon: <ShieldCheck className="h-3 w-3" />, color: "#F0ABFC", bg: "rgba(240,171,252,0.12)" },
  { value: "client_admin", label: "Client admin", icon: <UserCog className="h-3 w-3" />, color: "#A78BFA", bg: "rgba(167,139,250,0.12)" },
  { value: "trainer", label: "Trainer", icon: <Briefcase className="h-3 w-3" />, color: "#22D3EE", bg: "rgba(34,211,238,0.12)" },
  { value: "learner", label: "Learner", icon: <GraduationCap className="h-3 w-3" />, color: "#10B981", bg: "rgba(16,185,129,0.12)" },
];

function roleToken(role: string) {
  return ROLES.find((r) => r.value === role) ?? ROLES[3]!;
}

function fmtRelative(iso: string | null): string {
  if (iso === null) return "never";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

/**
 * AdminUsersPage — /admin/users — Step 10 user admin.
 *
 * Outer table = one row per user, with role pill + client + assignment/
 * attempt counts. Click row to expand → that user's sim assignments, with
 * per-row Unassign button and a "+ Assign sim" pad. Cohort name acts as
 * the group/segmentation tag (kept on each learner_assignments row).
 *
 * Super_admin only — every backend endpoint enforces that.
 */
export function AdminUsersPage(): JSX.Element {
  const navigate = useNavigate();

  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [clients, setClients] = useState<ClientOpt[]>([]);
  const [sims, setSims] = useState<SimOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("");
  const [clientFilter, setClientFilter] = useState<string>("");

  const [openUserId, setOpenUserId] = useState<string | null>(null);
  const [assignmentsByUser, setAssignmentsByUser] = useState<Record<string, AssignmentRow[]>>({});
  const [assignmentsLoading, setAssignmentsLoading] = useState<string | null>(null);

  const [editingUser, setEditingUser] = useState<UserRow | "new" | null>(null);
  const [deletingUser, setDeletingUser] = useState<UserRow | null>(null);
  const [assigningFor, setAssigningFor] = useState<UserRow | null>(null);
  const [unassigning, setUnassigning] = useState<{ user: UserRow; assignment: AssignmentRow } | null>(null);
  const [unassignBusy, setUnassignBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const loadUsers = async (): Promise<void> => {
    setLoading(true); setError(null);
    // 120s + one silent retry on abort — covers Azure Functions cold start
    // (same pattern as loadClients, useSimulations, useDashboardData,
    // attemptStore.fetchResults). Without this the page surfaces
    // "signal is aborted without reason" on first visit of a session.
    const call = (): Promise<{ users: UserRow[] }> =>
      apiCall<{ users: UserRow[] }>("/libraryUsers", {
        method: "POST",
        body: {
          query: query.trim() === "" ? undefined : query,
          role: roleFilter === "" ? undefined : roleFilter,
          clientId: clientFilter === "" ? undefined : clientFilter,
          includeArchived: true,
        },
        timeoutMs: 120_000,
      });
    try {
      let res: { users: UserRow[] };
      try {
        res = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        res = await call();
      }
      setUsers(res.users);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Couldn't load users");
    } finally { setLoading(false); }
  };

  const loadClients = async (): Promise<void> => {
    // 120s + one silent retry on abort — same shape as useSimulations and the
    // dashboard hooks. Default 25s trips on Azure Functions cold start, which
    // used to leave the modal's client dropdown silently empty and block
    // promoting anyone to client_admin.
    const call = (): Promise<{ clients: ClientOpt[] }> =>
      apiCall<{ clients: ClientOpt[] }>("/libraryClients", { timeoutMs: 120_000 });
    try {
      let res: { clients: ClientOpt[] };
      try {
        res = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        res = await call();
      }
      setClients(res.clients);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("loadClients failed:", err);
      setToast(`Couldn't load clients: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const loadSims = async (): Promise<void> => {
    // Same 120s + retry pattern as loadUsers/loadClients — the assignment
    // picker stays empty if this aborts on cold start.
    const call = (): Promise<{ sims: SimOpt[] }> =>
      apiCall<{ sims: SimOpt[] }>("/libraryList", { timeoutMs: 120_000 });
    try {
      let res: { sims: SimOpt[] };
      try {
        res = await call();
      } catch (e) {
        const isAbort = e instanceof Error && /abort/i.test(e.message);
        if (!isAbort) throw e;
        await new Promise((r) => setTimeout(r, 1000));
        res = await call();
      }
      setSims(res.sims);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("loadSims failed:", err);
    }
  };

  const loadAssignments = async (userId: string): Promise<void> => {
    setAssignmentsLoading(userId);
    try {
      const res = await apiCall<{ assignments: AssignmentRow[] }>("/libraryUserAssignments", {
        method: "POST", body: { userId },
      });
      setAssignmentsByUser((prev) => ({ ...prev, [userId]: res.assignments }));
    } catch (err) {
      setAssignmentsByUser((prev) => ({ ...prev, [userId]: [] }));
      console.error("loadAssignments", err);
    } finally { setAssignmentsLoading(null); }
  };

  useEffect(() => { void loadUsers(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // F-007 admin on-behalf DSAR export. Audit-logged server-side; super_admin
  // only. Downloads the full bundle as JSON to the admin's browser.
  const exportUserData = async (u: UserRow): Promise<void> => {
    try {
      const res = await apiCall<{ _meta?: { subject?: { email?: string } } }>(
        "/libraryUserDataExport",
        { method: "POST", body: { userId: u.id }, timeoutMs: 60_000 },
      );
      const subject = res._meta?.subject?.email ?? u.email;
      const safeName = String(subject).replace(/[^a-z0-9]+/gi, "_").toLowerCase();
      const stamp = new Date().toISOString().slice(0, 10);
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = `neusim-data-${safeName}-${stamp}.json`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setToast(`Exported data for ${u.email}`);
      setTimeout(() => setToast(null), 4000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Export failed";
      setToast(msg);
      setTimeout(() => setToast(null), 6000);
    }
  };
  useEffect(() => { void loadClients(); void loadSims(); }, []);

  // Debounced filter refetch
  useEffect(() => {
    const t = window.setTimeout(() => { void loadUsers(); }, 250);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, roleFilter, clientFilter]);

  const toggleExpand = (u: UserRow): void => {
    if (openUserId === u.id) { setOpenUserId(null); return; }
    setOpenUserId(u.id);
    if (assignmentsByUser[u.id] === undefined) void loadAssignments(u.id);
  };

  const handleUnassign = async (): Promise<void> => {
    if (unassigning === null) return;
    setUnassignBusy(true);
    try {
      await apiCall("/libraryUserUnassign", { method: "POST", body: { assignmentId: unassigning.assignment.id } });
      setToast(`Removed ${unassigning.assignment.simCode} from ${unassigning.user.email}`);
      await loadAssignments(unassigning.user.id);
      await loadUsers();
      setUnassigning(null);
      setTimeout(() => setToast(null), 3000);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Unassign failed";
      setToast(`Error: ${msg}`);
    } finally { setUnassignBusy(false); }
  };

  return (
    <ScreenShell>
      <SimHeader decision={users?.length ?? 0} xp={0} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <div className="mb-5"><AdminNav /></div>

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-fuchsia-300">
                <Users className="h-4 w-4" />
                <span className="text-[10px] font-bold uppercase tracking-[0.18em]">User Admin</span>
              </div>
              <h1 className="mt-2 text-[28px] font-bold leading-tight">
                <GradientText>Users &amp; role assignments</GradientText>
              </h1>
              <p className="mt-1 text-sm text-white/55">Pre-seed a learner&rsquo;s email + role + client; Entra fills the rest on first login. Expand a row to manage that user&rsquo;s sim assignments.</p>
            </div>
            <button
              type="button"
              onClick={() => setEditingUser("new")}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25"
            >
              <Plus className="h-4 w-4" /> New user
            </button>
          </div>
        </motion.div>

        {/* Filter bar */}
        <div className="mt-5 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-white/35" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name or email"
              className="w-72 rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 pl-8 text-[12px] text-white placeholder:text-white/30 outline-none focus:border-fuchsia-400/60"
            />
          </div>
          <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="rounded-lg border border-white/15 bg-[#0c0e22] px-3 py-1.5 text-[12px] text-white outline-none focus:border-fuchsia-400/60">
            <option value="">All roles</option>
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <select value={clientFilter} onChange={(e) => setClientFilter(e.target.value)} className="rounded-lg border border-white/15 bg-[#0c0e22] px-3 py-1.5 text-[12px] text-white outline-none focus:border-fuchsia-400/60">
            <option value="">All clients</option>
            {clients.filter((c) => c.isActive).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {toast !== null && (
          <div className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] text-emerald-200">
            <CheckCircle2 className="h-3.5 w-3.5" /> {toast}
          </div>
        )}

        {loading && <p className="mt-6 text-sm text-white/45">Loading users…</p>}
        {!loading && error !== null && (
          <GlassCard className="mx-auto mt-8 max-w-lg p-8 text-center">
            <AlertCircle className="mx-auto h-9 w-9 text-[#f87272]" />
            <p className="mt-3 text-sm text-white/70">{error}</p>
          </GlassCard>
        )}

        {!loading && error === null && users !== null && users.length === 0 && (
          <GlassCard className="mt-6 p-10 text-center">
            <p className="text-sm text-white/55">No users match these filters.</p>
          </GlassCard>
        )}

        {!loading && error === null && users !== null && users.length > 0 && (
          <GlassCard className="mt-6 overflow-hidden p-0">
            <table className="w-full table-fixed text-[12.5px]">
              <colgroup>
                <col style={{ width: "2.5rem" }} />
                <col />
                <col style={{ width: "8rem" }} />
                <col style={{ width: "9rem" }} />
                <col style={{ width: "4.5rem" }} />
                <col style={{ width: "4.5rem" }} />
                <col style={{ width: "6.5rem" }} />
                <col style={{ width: "8rem" }} />
              </colgroup>
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02] text-[10px] uppercase tracking-[0.12em] text-white/40">
                  <th></th>
                  <th className="px-3 py-2.5 text-left">User</th>
                  <th className="px-3 py-2.5 text-left">Role</th>
                  <th className="px-3 py-2.5 text-left">Client</th>
                  <th className="px-3 py-2.5 text-right">Sims</th>
                  <th className="px-3 py-2.5 text-right">Attempts</th>
                  <th className="px-3 py-2.5 text-left">Last login</th>
                  <th className="px-3 py-2.5 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const isOpen = openUserId === u.id;
                  const r = roleToken(u.role);
                  const assignments = assignmentsByUser[u.id] ?? null;
                  return (
                    <RowGroup key={u.id}>
                      <tr
                        onClick={() => toggleExpand(u)}
                        className={`cursor-pointer border-b border-white/5 hover:bg-white/[0.03] ${!u.isActive ? "opacity-50" : ""}`}
                      >
                        <td className="px-1 py-2.5 text-center text-white/40">
                          <ChevronRight className={`inline h-3.5 w-3.5 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                        </td>
                        <td className="px-3 py-2.5 truncate">
                          <div className="truncate font-medium text-white">{u.fullName}</div>
                          <div className="truncate text-[10.5px] text-white/45">{u.email}</div>
                        </td>
                        <td className="px-3 py-2.5">
                          <span className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[9.5px] font-bold uppercase tracking-[0.1em]" style={{ background: r.bg, borderColor: `${r.color}66`, color: r.color }}>
                            {r.icon} {r.label}
                          </span>
                        </td>
                        <td className="px-3 py-2.5 truncate text-white/75">{u.clientName ?? <span className="text-white/30">—</span>}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{u.assignmentCount}</td>
                        <td className="px-3 py-2.5 text-right font-mono text-white/80">{u.attemptCount}</td>
                        <td className="px-3 py-2.5 text-[11.5px] text-white/55">{fmtRelative(u.lastLoginAt)}</td>
                        <td className="px-3 py-2.5 text-right">
                          <div className="inline-flex items-center gap-1.5">
                            <button type="button" onClick={(e) => { e.stopPropagation(); setEditingUser(u); }} className="inline-flex items-center gap-1 rounded-md border border-white/15 bg-white/5 px-2 py-1 text-[10.5px] text-white/75 hover:border-white/30">
                              <Pencil className="h-3 w-3" /> Edit
                            </button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); void exportUserData(u); }}
                                    title="Export this user's data (DSAR / GDPR right of access)"
                                    className="inline-flex items-center rounded-md border border-emerald-400/30 bg-emerald-500/5 px-1.5 py-1 text-emerald-300 hover:border-emerald-400/60 hover:bg-emerald-500/15">
                              <Download className="h-3 w-3" />
                            </button>
                            <button type="button" onClick={(e) => { e.stopPropagation(); setDeletingUser(u); }}
                                    title="Delete user (archive or purge)"
                                    className="inline-flex items-center rounded-md border border-red-400/30 bg-red-500/5 px-1.5 py-1 text-red-300 hover:border-red-400/60 hover:bg-red-500/15">
                              <Trash2 className="h-3 w-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-white/5 bg-white/[0.02]">
                          <td></td>
                          <td colSpan={7} className="px-3 py-3">
                            <div className="flex items-center justify-between gap-3">
                              <h3 className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-cyan-200">
                                <Layers className="mr-1 inline h-3 w-3" /> Sim assignments ({assignments?.filter((a) => a.isActive).length ?? 0})
                              </h3>
                              <button type="button" onClick={() => setAssigningFor(u)} className="inline-flex items-center gap-1 rounded-md border border-fuchsia-400/30 bg-fuchsia-500/10 px-2 py-1 text-[10.5px] font-semibold text-fuchsia-200 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/15">
                                <Plus className="h-3 w-3" /> Assign sim
                              </button>
                            </div>
                            {assignmentsLoading === u.id && <p className="mt-2 text-[11px] text-white/45">Loading…</p>}
                            {assignmentsLoading !== u.id && assignments !== null && assignments.length === 0 && (
                              <p className="mt-2 text-[11px] text-white/45">No sims assigned yet.</p>
                            )}
                            {assignments !== null && assignments.length > 0 && (
                              <div className="mt-2 space-y-1.5">
                                {assignments.map((a) => (
                                  <div key={a.id} className={`grid grid-cols-[1fr_auto_auto_auto_auto] items-center gap-3 rounded-md border border-white/8 bg-white/[0.02] px-3 py-2 ${!a.isActive ? "opacity-50" : ""}`}>
                                    <div className="min-w-0 truncate">
                                      <div className="truncate text-[12px] font-medium text-white">
                                        <span className="font-mono text-[10px] text-white/40">{a.simCode}</span> · {a.simTitle}
                                      </div>
                                      <div className="truncate text-[10.5px] text-white/45">{a.clientName} · {a.projectName}</div>
                                    </div>
                                    <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] font-semibold text-cyan-200" title="Cohort / security group">
                                      {a.cohortName}
                                    </span>
                                    <span className="font-mono text-[11px] text-white/65">{a.attemptCount} att</span>
                                    {a.isActive ? (
                                      <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase text-emerald-300">Active</span>
                                    ) : (
                                      <span className="rounded-full border border-white/15 bg-white/[0.05] px-1.5 py-0.5 text-[9px] font-bold uppercase text-white/45">Inactive</span>
                                    )}
                                    {a.isActive && (
                                      <button type="button" onClick={() => setUnassigning({ user: u, assignment: a })} className="inline-flex items-center gap-1 rounded-md border border-rose-400/30 bg-rose-500/10 px-2 py-1 text-[10.5px] font-semibold text-rose-200 hover:border-rose-400/60 hover:bg-rose-500/20" title="Unassign — sim hides from /simulations; attempt history kept">
                                        <X className="h-3 w-3" /> Unassign
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </RowGroup>
                  );
                })}
              </tbody>
            </table>
          </GlassCard>
        )}

        {editingUser !== null && (
          <UserEditModal
            initial={editingUser === "new" ? null : editingUser}
            clients={clients}
            onClose={() => setEditingUser(null)}
            onSaved={() => { setEditingUser(null); void loadUsers(); }}
          />
        )}

        {deletingUser !== null && (
          <DeleteUserModal
            user={deletingUser}
            onClose={() => setDeletingUser(null)}
            onDone={(msg: string) => {
              setDeletingUser(null);
              void loadUsers();
              setToast(msg);
              setTimeout(() => setToast(null), 5000);
            }}
          />
        )}

        {assigningFor !== null && (
          <AssignSimModal
            user={assigningFor}
            sims={sims}
            existing={assignmentsByUser[assigningFor.id] ?? []}
            onClose={() => setAssigningFor(null)}
            onSaved={() => {
              const u = assigningFor;
              setAssigningFor(null);
              void loadAssignments(u.id);
              void loadUsers();
              setToast(`Sim assigned to ${u.email}`);
              setTimeout(() => setToast(null), 3000);
            }}
          />
        )}

        {unassigning !== null && (
          <div onClick={() => { if (!unassignBusy) setUnassigning(null); }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4">
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md overflow-hidden rounded-2xl border border-rose-400/30 bg-gradient-to-br from-[#1a0a16] to-[#0c0810] text-white shadow-2xl">
              <div className="border-b border-white/10 px-5 py-4">
                <h2 className="flex items-center gap-2 text-base font-bold text-rose-200">
                  <X className="h-4 w-4" /> Unassign sim?
                </h2>
              </div>
              <div className="px-5 py-4 text-[13px] leading-relaxed text-white/80">
                <p>
                  Remove <span className="font-mono text-rose-200">{unassigning.assignment.simCode}</span> from <span className="font-medium">{unassigning.user.email}</span>?
                </p>
                <ul className="mt-3 space-y-1 text-[11.5px] text-white/55">
                  <li>• Cohort: <span className="font-mono">{unassigning.assignment.cohortName}</span></li>
                  <li>• {unassigning.assignment.attemptCount} attempt{unassigning.assignment.attemptCount === 1 ? "" : "s"} on record (KEPT — only the access flag is cleared)</li>
                  <li>• Sim disappears from /simulations for this learner</li>
                  <li>• Re-assign at any time to restore access</li>
                </ul>
              </div>
              <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3">
                <button type="button" onClick={() => setUnassigning(null)} disabled={unassignBusy} className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40">Cancel</button>
                <button type="button" onClick={() => void handleUnassign()} disabled={unassignBusy} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-400/40 bg-rose-500/20 px-4 py-1.5 text-[12px] font-semibold text-rose-100 hover:border-rose-400/60 hover:bg-rose-500/30 disabled:cursor-not-allowed disabled:opacity-40">
                  <X className="h-3.5 w-3.5" /> {unassignBusy ? "Unassigning…" : "Unassign"}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </ScreenShell>
  );
}

function RowGroup({ children }: { children: React.ReactNode }): JSX.Element { return <>{children}</>; }

interface DeleteSummary {
  appAction: "archived" | "purged";
  entraStatus: "deleted" | "already_gone" | "no_oid" | "failed";
  entraReason?: string;
  affectedCohorts: number;
  cascade?: { attempts: number; cohortMembers: number; learnerAssignments: number; clientAdminProjects: number };
}

function DeleteUserModal({ user, onClose, onDone }: { user: UserRow; onClose: () => void; onDone: (msg: string) => void }): JSX.Element {
  const [mode, setMode] = useState<"archive" | "purge">("archive");
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DeleteSummary | null>(null);

  const confirmNeeded = mode === "purge";
  const canSubmit = !busy && (!confirmNeeded || confirmText.trim().toLowerCase() === user.email.toLowerCase());

  const submit = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiCall<{ ok: true; summary: DeleteSummary }>(
        "/libraryUserDelete", { method: "POST", body: { userId: user.id, mode }, timeoutMs: 60_000 },
      );
      setResult(res.summary);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : "Delete failed");
    } finally { setBusy(false); }
  };

  // Result view
  if (result !== null) {
    const entraTone =
      result.entraStatus === "deleted"      ? "border-emerald-400/40 bg-emerald-500/10 text-emerald-200" :
      result.entraStatus === "already_gone" ? "border-white/15 bg-white/5 text-white/65" :
      result.entraStatus === "no_oid"       ? "border-amber-400/30 bg-amber-500/10 text-amber-200" :
                                              "border-red-400/40 bg-red-500/10 text-red-200";
    const entraLabel =
      result.entraStatus === "deleted"      ? "Deleted from Entra" :
      result.entraStatus === "already_gone" ? "Already gone from Entra" :
      result.entraStatus === "no_oid"       ? "No Entra identity found" :
                                              "Entra delete failed";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
        <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
          <h3 className="mb-1 text-lg font-bold">Done</h3>
          <p className="mb-4 text-xs text-white/60">{user.fullName} ({user.email}) has been {result.appAction}.</p>

          <div className={`mb-3 rounded-lg border px-3 py-2 text-xs ${entraTone}`}>
            <div className="font-semibold">{entraLabel}</div>
            {result.entraReason !== undefined && <div className="mt-0.5 text-[11px] opacity-80">{result.entraReason}</div>}
          </div>

          {result.appAction === "purged" && result.cascade !== undefined && (
            <div className="mb-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[11px] text-white/70">
              <div className="mb-1 font-semibold text-white/90">Cascade</div>
              <div>· {result.cascade.attempts} attempt rows removed</div>
              <div>· {result.cascade.cohortMembers} cohort memberships removed</div>
              <div>· {result.cascade.learnerAssignments} legacy assignments removed</div>
              {result.cascade.clientAdminProjects > 0 && <div>· {result.cascade.clientAdminProjects} project grants removed</div>}
            </div>
          )}

          <div className="mb-4 text-[11px] text-white/55">{result.affectedCohorts} cohort{result.affectedCohorts === 1 ? "" : "s"} re-synced.</div>

          <div className="flex justify-end">
            <button type="button" onClick={() => onDone(`${user.email} ${result.appAction}`)}
                    className="rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/20 px-4 py-2 text-sm font-semibold text-fuchsia-100 hover:bg-fuchsia-500/30">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-neusim-bg p-6 text-white" onClick={(e) => e.stopPropagation()}>
        <h3 className="mb-1 text-lg font-bold">Delete user</h3>
        <p className="mb-4 text-xs text-white/60">{user.fullName} <span className="text-white/40">·</span> {user.email}</p>

        <div className="mb-4 space-y-2">
          <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${mode === "archive" ? "border-emerald-400/40 bg-emerald-500/5" : "border-white/10 bg-white/[0.02] hover:bg-white/5"}`}>
            <input type="radio" name="del-mode" checked={mode === "archive"} onChange={() => setMode("archive")} className="mt-1 accent-emerald-400" />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-emerald-200"><Archive className="h-3.5 w-3.5" /> Archive</div>
              <div className="mt-0.5 text-[11px] text-white/55">Keep their attempts and cohort history in the DB (dashboards still work). Revoke login by hard-deleting from Entra. Reversible if you ever need them back.</div>
            </div>
          </label>
          <label className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition-colors ${mode === "purge" ? "border-red-400/50 bg-red-500/5" : "border-white/10 bg-white/[0.02] hover:bg-white/5"}`}>
            <input type="radio" name="del-mode" checked={mode === "purge"} onChange={() => setMode("purge")} className="mt-1 accent-red-400" />
            <div className="flex-1">
              <div className="flex items-center gap-1.5 text-sm font-semibold text-red-200"><Trash2 className="h-3.5 w-3.5" /> Permanently delete</div>
              <div className="mt-0.5 text-[11px] text-white/55">Wipe their row + all attempts, memberships and assignments. Past cohort analytics lose their data. Plus hard-delete from Entra. NOT REVERSIBLE.</div>
            </div>
          </label>
        </div>

        {confirmNeeded && (
          <label className="mb-3 block">
            <span className="text-[11px] uppercase tracking-wider text-amber-200/70">Type the email to confirm</span>
            <div className="mt-1 flex items-center gap-1.5 text-[10.5px] text-amber-300/80"><AlertTriangle className="h-3 w-3" /> This cannot be undone.</div>
            <input type="text" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={user.email} autoFocus
                   className="mt-1.5 w-full rounded-lg border border-red-400/30 bg-red-500/5 px-3 py-2 font-mono text-xs text-white outline-none focus:border-red-400/60" />
          </label>
        )}

        {error !== null && <div className="mb-3 rounded-lg border border-red-400/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">{error}</div>}

        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-white/10 bg-white/5 px-4 py-2 text-sm text-white/80 hover:bg-white/10">Cancel</button>
          <button type="button" disabled={!canSubmit} onClick={() => void submit()}
                  className={`rounded-lg border px-4 py-2 text-sm font-semibold disabled:opacity-50 ${mode === "purge" ? "border-red-400/40 bg-red-500/20 text-red-100 hover:bg-red-500/30" : "border-emerald-400/40 bg-emerald-500/20 text-emerald-100 hover:bg-emerald-500/30"}`}>
            {busy ? "Working…" : mode === "archive" ? "Archive user" : "Delete forever"}
          </button>
        </div>
      </div>
    </div>
  );
}

interface ProjectChoice { id: string; name: string; clientId: string; isActive: boolean }

function UserEditModal({ initial, clients, onClose, onSaved }: { initial: UserRow | null; clients: ClientOpt[]; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [email, setEmail] = useState(initial?.email ?? "");
  const [fullName, setFullName] = useState(initial?.fullName ?? "");
  const [role, setRole] = useState(initial?.role ?? "learner");
  const [clientId, setClientId] = useState<string | "">(initial?.clientId ?? "");
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Phase K2: project access for client_admin. Pre-filled from libraryUsers'
  // projectIds field on edit; an empty list on first promotion to
  // client_admin means "use the default" (libraryUserSave then auto-grants
  // all projects under the client — K1 fallback). The picker explicitly
  // tracks "untouched" to preserve the K1 default; once the user clicks any
  // checkbox we send projectIds explicitly.
  const [projectIds, setProjectIds] = useState<Set<string>>(() => new Set(initial?.projectIds ?? []));
  const [projectsTouched, setProjectsTouched] = useState(false);
  const [allProjects, setAllProjects] = useState<ProjectChoice[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  const needsClient = role !== "super_admin";
  const showProjectPicker = role === "client_admin" && clientId !== "";

  // Load projects under the selected client whenever it changes. We fetch ALL
  // (no clientId filter sends every project the caller can see — super_admin
  // gets the lot), then filter by clientId here.
  useEffect(() => {
    if (!showProjectPicker) return;
    let alive = true;
    setProjectsLoading(true);
    (async (): Promise<void> => {
      try {
        const res = await apiCall<{ projects: ProjectChoice[] }>("/libraryProjects", {
          method: "POST", body: { clientId }, timeoutMs: 120_000,
        });
        if (alive) setAllProjects(res.projects);
      } catch { if (alive) setAllProjects([]); }
      finally { if (alive) setProjectsLoading(false); }
    })();
    return () => { alive = false; };
  }, [clientId, showProjectPicker]);

  // When client changes, drop any project picks from the previous client.
  useEffect(() => {
    if (!projectsTouched) return;
    const validIds = new Set(allProjects.filter((p) => p.clientId === clientId).map((p) => p.id));
    setProjectIds((prev) => new Set([...prev].filter((id) => validIds.has(id))));
  }, [clientId, allProjects, projectsTouched]);

  const handleSave = async (): Promise<void> => {
    setError(null);
    if (email.trim() === "") { setError("Email is required"); return; }
    if (fullName.trim() === "") { setError("Full name is required"); return; }
    if (needsClient && (clientId === "" || clientId === null)) { setError(`${role} users must belong to a client`); return; }
    setBusy(true);
    try {
      const baseBody: Record<string, unknown> = {
        email, fullName, role,
        clientId: needsClient ? clientId : null,
      };
      // Only send projectIds when the picker was touched OR the user already
      // had explicit grants we're keeping unchanged. Otherwise let the K1
      // auto-grant run server-side so the first save isn't surprising.
      if (role === "client_admin" && (projectsTouched || (initial?.projectIds?.length ?? 0) > 0)) {
        baseBody["projectIds"] = Array.from(projectIds);
      }
      const body = initial === null
        ? baseBody
        : { id: initial.id, ...baseBody, isActive };
      await apiCall("/libraryUserSave", { method: "POST", body });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Save failed");
    } finally { setBusy(false); }
  };

  const toggleProject = (id: string): void => {
    setProjectsTouched(true);
    setProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const projectsForClient = allProjects.filter((p) => p.clientId === clientId && (p.isActive || projectIds.has(p.id)));

  return (
    <ModalShell title={initial === null ? "New user" : `Edit ${initial.fullName}`} busy={busy} onClose={onClose}>
      <div className="space-y-3">
        <ModalField label="Email *"><input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="learner@example.com" className={inputClass} /></ModalField>
        <ModalField label="Full name *" hint="Entra will overwrite on first login.">
          <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="e.g. Priya Sharma" className={inputClass} />
        </ModalField>
        <div className="grid grid-cols-2 gap-3">
          <ModalField label="Role">
            <select value={role} onChange={(e) => setRole(e.target.value)} className={inputClass}>
              {ROLES.map((r) => <option key={r.value} value={r.value} className="bg-[#0c0e22]">{r.label}</option>)}
            </select>
          </ModalField>
          {needsClient ? (
            <ModalField label="Client *">
              <select value={clientId} onChange={(e) => setClientId(e.target.value)} className={inputClass}>
                <option value="" className="bg-[#0c0e22]">— select —</option>
                {clients.filter((c) => c.isActive || c.id === clientId).map((c) => (
                  <option key={c.id} value={c.id} className="bg-[#0c0e22]">{c.name}{!c.isActive ? " (archived)" : ""}</option>
                ))}
              </select>
            </ModalField>
          ) : (
            <ModalField label="Client"><div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2 text-[12px] text-white/40 italic">super_admin spans all clients</div></ModalField>
          )}
        </div>

        {showProjectPicker && (
          <ModalField label="Projects this admin can manage" hint={projectsTouched ? `${projectIds.size} of ${projectsForClient.length} selected` : "Leave untouched to grant access to all projects under the client."}>
            {projectsLoading ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-3 text-[12px] text-white/40 italic">Loading projects…</div>
            ) : projectsForClient.length === 0 ? (
              <div className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-3 text-[12px] text-white/40 italic">No projects under this client yet.</div>
            ) : (
              <div className="rounded-lg border border-white/10 bg-white/[0.02]">
                <div className="flex items-center justify-between border-b border-white/5 px-3 py-2 text-[10px] uppercase tracking-wider text-white/40">
                  <span>{projectIds.size} of {projectsForClient.length} selected</span>
                  <div className="flex gap-3">
                    <button type="button" className="text-fuchsia-300 hover:text-fuchsia-200"
                      onClick={() => { setProjectsTouched(true); setProjectIds(new Set(projectsForClient.map((p) => p.id))); }}>Select all</button>
                    <button type="button" className="text-white/50 hover:text-white/80"
                      onClick={() => { setProjectsTouched(true); setProjectIds(new Set()); }}>Clear</button>
                  </div>
                </div>
                <div className="max-h-44 overflow-y-auto px-2 py-1">
                  {projectsForClient.map((p) => (
                    <label key={p.id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-[12px] text-white/85 hover:bg-white/5">
                      <input type="checkbox" checked={projectIds.has(p.id)} onChange={() => toggleProject(p.id)} className="accent-fuchsia-400" />
                      <span className="flex-1">{p.name}</span>
                      {!p.isActive && <span className="text-[10px] uppercase tracking-wide text-white/40">archived</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}
          </ModalField>
        )}

        {initial !== null && (
          <label className="flex items-center gap-2 text-[12px] text-white/65">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} className="accent-fuchsia-400" />
            Active — uncheck to archive (login fails, but historical data is preserved)
          </label>
        )}
        {error !== null && <p className="text-[11px] text-rose-300"><AlertCircle className="mr-1 inline h-3 w-3" />{error}</p>}
      </div>
      <ModalFooter onCancel={onClose} onSave={() => void handleSave()} busy={busy} saveLabel={initial === null ? "Create user" : "Save changes"} />
    </ModalShell>
  );
}

function AssignSimModal({ user, sims, existing, onClose, onSaved }: { user: UserRow; sims: SimOpt[]; existing: AssignmentRow[]; onClose: () => void; onSaved: () => void }): JSX.Element {
  const [simId, setSimId] = useState<string>("");
  const [cohortName, setCohortName] = useState<string>("Default");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const existingPairs = useMemo(() => new Set(existing.filter((a) => a.isActive).map((a) => `${a.simulationId}|${a.cohortName}`)), [existing]);
  const dupe = simId !== "" && existingPairs.has(`${simId}|${cohortName.trim()}`);

  const handleSave = async (): Promise<void> => {
    if (simId === "") { setError("Pick a sim"); return; }
    if (cohortName.trim() === "") { setError("Cohort name is required"); return; }
    setBusy(true); setError(null);
    try {
      await apiCall("/libraryUserAssign", {
        method: "POST",
        body: { userId: user.id, simulationId: simId, cohortName: cohortName.trim() },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Assign failed");
    } finally { setBusy(false); }
  };

  return (
    <ModalShell title={`Assign sim to ${user.fullName}`} busy={busy} onClose={onClose}>
      <div className="space-y-3">
        <ModalField label="Simulation *">
          <select value={simId} onChange={(e) => setSimId(e.target.value)} className={inputClass}>
            <option value="" className="bg-[#0c0e22]">— pick a sim —</option>
            {sims.map((s) => (
              <option key={s.id} value={s.id} className="bg-[#0c0e22]">
                {s.clientName !== null ? `${s.clientName} · ` : ""}{s.projectName !== null ? `${s.projectName} · ` : ""}{s.simCode} — {s.title}
              </option>
            ))}
          </select>
        </ModalField>
        <ModalField label="Cohort / security group *" hint="Tag for cohort-level analytics (e.g. &quot;Cohort A&quot;, &quot;Managers&quot;).">
          <input value={cohortName} onChange={(e) => setCohortName(e.target.value)} placeholder="e.g. Cohort A" className={inputClass} />
        </ModalField>
        {dupe && (
          <p className="text-[11px] text-amber-300/85">
            Already assigned with this cohort label. Submitting will re-activate the existing row.
          </p>
        )}
        {error !== null && <p className="text-[11px] text-rose-300"><AlertCircle className="mr-1 inline h-3 w-3" />{error}</p>}
      </div>
      <ModalFooter onCancel={onClose} onSave={() => void handleSave()} busy={busy} saveLabel="Assign" />
    </ModalShell>
  );
}

const inputClass = "w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2 text-sm text-white outline-none focus:border-fuchsia-400/60";

function ModalField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }): JSX.Element {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/55">{label}</label>
      {hint !== undefined && <span className="ml-2 text-[10px] text-white/35">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ModalShell({ title, busy, onClose, children }: { title: string; busy: boolean; onClose: () => void; children: React.ReactNode }): JSX.Element {
  useEffect(() => {
    const h = (e: KeyboardEvent): void => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h);
  }, [busy, onClose]);
  return (
    <div onClick={() => { if (!busy) onClose(); }} className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm px-4 py-10">
      <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-[#0c0e22] to-[#070818] text-white shadow-2xl">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
        <div className="border-b border-white/10 px-6 py-4"><h2 className="text-lg font-bold">{title}</h2></div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onCancel, onSave, busy, saveLabel }: { onCancel: () => void; onSave: () => void; busy: boolean; saveLabel: string }): JSX.Element {
  return (
    <div className="mt-5 flex items-center justify-end gap-2 border-t border-white/10 pt-4">
      <button type="button" onClick={onCancel} disabled={busy} className="rounded-lg border border-white/15 bg-white/5 px-3 py-1.5 text-[12px] text-white/70 hover:border-white/30 disabled:opacity-40">Cancel</button>
      <button type="button" onClick={onSave} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-fuchsia-400/40 bg-fuchsia-500/15 px-4 py-1.5 text-[12px] font-semibold text-fuchsia-100 hover:border-fuchsia-400/60 hover:bg-fuchsia-500/25 disabled:cursor-not-allowed disabled:opacity-40">
        {busy ? <RotateCcw className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
        {busy ? "Saving…" : saveLabel}
      </button>
    </div>
  );
}
